import useCdcProps from '@/composables/useCdcProps'
import AppService from './appService'
import type {
  Module,
  ModuleBasic,
  ModuleCategoryFromApi,
  ModuleFromApi,
  ModuleFromApiV2,
  ModuleFull,
  ModuleStatus,
} from '@/types/modules.interfaces'

import { authoriseModule } from '@/utils/modulesBlackList'
import {
  moduleV1Mapper,
  moduleV2fromApiV1Mapper,
  moduleV2MapperFull,
  moduleV2Mapper,
  searchMapper,
} from '@/utils/modulesMapper'

import { moduleToSubscriptionMapper } from '@/utils/subscriptionMapper'

import type {
  Subscription,
  ModuleWithSubscriptionFromApi,
} from '@/types/subscriptions.interfaces'
import type { ContextStore } from '@/stores/context'
export interface ActionResponse {
  config_url: string
  version: string
  module_status?: ModuleStatus
}

export interface FetchSearchOpts {
  currency?: string
  keyword?: string
  isoLang?: string
  isoCode?: string
  psVersion?: string
  categoryId?: string
  developerId?: string
  page?: number
  pageSize?: number
  sortBy?: string
  rating?: number
  verifiedPlus?: boolean
}

type ActionExecutor = 'shop' | 'mboApi'

export default class ModuleService extends AppService {
  constructor(appContext: ContextStore) {
    super(appContext)
  }

  get addonsCredentialParams() {
    return {
      addons_pwd: this.context.addonsPassword,
      addons_username: this.context.addonsUsername,
    }
  }

  get accountsCredentialParams() {
    return {
      accounts_token: this.context.accountsToken,
    }
  }

  get baseContextParams() {
    return {
      iso_lang: this.context.isoLang,
      iso_code: this.context.isoCode,
      shop_url: this.context.shopUrl,
      ps_version: this.context.shopVersion,
      currency: this.context.currency,
      ...this.accountsCredentialParams,
      ...this.addonsCredentialParams,
    }
  }

  async fetchAll(): Promise<Module[]> {
    const params = { ...this.baseContextParams }

    return await this.mboApiV1
      .get('modules', { params })
      .then((res) => {
        return res.data.filter(authoriseModule).map(moduleV1Mapper)
      })
      .catch((err) => {
        throw err
      })
  }

  async fetchRecommended(): Promise<Module[]> {
    const params = {
      ...this.baseContextParams,
      class_name: this.context.prestaShopControllerClassName,
    }

    return await this.mboApiV1
      .get('modules/recommendations', { params })
      .then((res) => {
        return res.data.filter(authoriseModule).map(moduleV1Mapper)
      })
      .catch((err) => {
        throw err
      })
  }

  async fetch(
    id: string | number,
    context: any = null,
    category: any = null,
    purchased = false,
  ): Promise<ModuleFull> {
    const params = context ?? { ...this.baseContextParams }

    return await this.mboApiV1
      .get(`modules/${id}`, { params })
      .then((res) => {
        return moduleV2MapperFull(
          { ...res.data, purchased },
          params.ps_version as string,
          category,
        ) as any
      })
      .catch((err) => {
        throw err
      })
  }

  async fetchByName(
    name: string,
    query: any = null,
    category: any = null,
    purchased = false,
  ): Promise<ModuleFull> {
    const params = {
      ...{
        iso_lang: this.context.isoLang,
        iso_code: this.context.isoCode,
        currency: this.context.currency,
        ps_version: this.context.shopVersion,
      },
      ...query,
    }

    return await this.mboApiV1
      .get(`modules/by_name/${name}`, { params })
      .then((res) => {
        return moduleV2MapperFull(
          { ...res.data, purchased },
          params.ps_version,
          category,
        ) as any
      })
      .catch((err) => {
        throw err
      })
  }

  async action(
    action: string,
    module: Module | ModuleFull,
  ): Promise<ActionResponse> {
    const params = {
      action,
      module: module.name,
      module_id: module.id,
      iso_lang: this.context.isoLang,
      iso_code: this.context.isoCode,
      ps_version: this.context.shopVersion,
      module_version: module.version,
      installed_version: module.installedVersion,
      admin_token: this.context.adminToken,
      shop_uuid: this.context.shopUuid,
      user_id: this.context.userId?.toString(),
      ...this.accountsCredentialParams,
      ...this.addonsCredentialParams,
    }

    try {
      const res = await this.mboApiV1.post('shops/action', params, {
        timeout: 59900,
      })
      return res.data as ActionResponse
    } catch (err) {
      throw err
    }
  }

  async fetchAllCategories(): Promise<Record<number, ModuleCategoryFromApi>> {
    return await this.mboApiV1
      .get('modules/categories', { params: this.baseContextParams })
      .then((res) => {
        return res.data
      })
      .catch((err) => {
        throw err
      })
  }

  async fetchSearch(options: FetchSearchOpts): Promise<any> {
    const {
      currency = this.context.currency,
      keyword,
      isoLang = this.context.isoLang,
      isoCode = this.context.isoCode,
      psVersion = this.context.shopVersion,
      categoryId,
      developerId,
      page = 1,
      pageSize = 24,
      sortBy,
      rating,
    } = options

    const res = await this.mboApiV1.get('modules/search', {
      params: {
        currency,
        id_category: categoryId,
        iso_code: isoCode,
        iso_lang: isoLang,
        nb_items_per_page: pageSize,
        page,
        ps_version: psVersion,
        query: keyword || '',
        shop_url: this.context.shopUrl,
        sorting_code: sortBy,
        developed_by: developerId,
        rating,
      },
    })

    if (res?.data.success) {
      return searchMapper(res.data)
    } else {
      return {
        categories: [],
        pagination: {
          currentPage: 1,
          total: 0,
        },
        modules: [],
        moduleSortedByNames: [],
        sortOpts: [],
        sortBy: sortBy,
        developers: [],
        ratingOpts: [],
      }
    }
  }

  async fetchPurchased(): Promise<ModuleBasic[]> {
    const { xprops } = useCdcProps()

    const actionExecutor: ActionExecutor =
      xprops?.context.mbo_version < '3.0.0' ? 'shop' : 'mboApi'

    let modules = null
    if (actionExecutor === 'shop') {
      modules = await xprops?.handleGetAddonsPurchasedModules()
    } else {
      const params = { ...this.baseContextParams }

      const res = await this.mboApiV1.get('modules/purchased', { params })

      modules = res.data
    }

    return modules
      ?.filter(authoriseModule)
      .map((i: ModuleFromApi) =>
        moduleV2fromApiV1Mapper(i, this.context.currency),
      )
  }

  async fetchPurchasedV2(): Promise<fetchPurchasedV2Response> {
    const params = {
      iso_lang: this.context.isoLang,
      iso_code: this.context.isoCode,
      shop_url: this.context.shopUrl,
      ps_version: this.context.shopVersion,
      ...this.accountsCredentialParams,
    }

    const res = await this.mboApiV1.get('modules/v2/purchased', { params })
    const modules = res.data
    return {
      modules: modules
        ?.filter(authoriseModule)
        .map((i: ModuleFromApi) =>
          moduleV2fromApiV1Mapper(i, this.context.currency),
        ),
      subscriptions: modules.map((m: ModuleWithSubscriptionFromApi) =>
        moduleToSubscriptionMapper(m, this.context.shopVersion as string),
      ),
    }
  }

  async fetchAutocomplete(query: string): Promise<any> {
    const params = {
      ps_version: this.context.shopVersion,
      query,
      iso_lang: this.context.isoLang,
      iso_code: this.context.isoCode,
    }

    const res = await this.mboApiV1.get('modules/autocomplete', { params })
    return res.data.results
  }

  async fetchBlock(block: string, options?: FetchSearchOpts): Promise<any> {
    const {
      currency = this.context.currency,
      keyword,
      isoLang = this.context.isoLang,
      isoCode = this.context.isoCode,
      psVersion = this.context.shopVersion,
      categoryId,
      developerId,
      page = 1,
      pageSize = 24,
      sortBy,
      rating,
    } = options || {}

    const res = await this.mboApiV1.get(`modules/blocks/${block}`, {
      params: {
        currency,
        id_category: categoryId,
        iso_code: isoCode,
        iso_lang: isoLang,
        nb_items_per_page: pageSize,
        page,
        ps_version: psVersion,
        query: keyword || '',
        shop_url: this.context.shopUrl,
        sorting_code: sortBy,
        developed_by: developerId,
        rating,
      },
    })

    return Object.values(res.data.results).map((module) =>
      moduleV2Mapper(module),
    )
  }

  async fetchBlocks(
    options?: FetchSearchOpts,
  ): Promise<Record<any, ModuleFull[]>> {
    const {
      currency = this.context.currency,
      keyword,
      isoLang = this.context.isoLang,
      isoCode = this.context.isoCode,
      psVersion = this.context.shopVersion,
      categoryId,
      developerId,
      page = 1,
      pageSize = 24,
      sortBy,
      rating,
    } = options || {}

    const res = await this.mboApiV1.get(`modules/blocks`, {
      params: {
        currency,
        id_category: categoryId,
        iso_code: isoCode,
        iso_lang: isoLang,
        nb_items_per_page: pageSize,
        page,
        ps_version: psVersion,
        query: keyword || '',
        shop_url: this.context.shopUrl,
        sorting_code: sortBy,
        developed_by: developerId,
        rating,
      },
    })

    const data: { [key: string]: ModuleFull[] } = {},
      blocks: { [key: string]: ModuleFromApiV2[] } = res.data.results

    for (const blockName in blocks) {
      if (Object.prototype.hasOwnProperty.call(blocks, blockName)) {
        const block = blocks[blockName]
        if (!data[blockName]) data[blockName] = []
        for (const moduleId in block) {
          if (Object.prototype.hasOwnProperty.call(block, moduleId)) {
            const module = block[moduleId]
            data[blockName].push(moduleV2Mapper(module))
          }
        }
      }
    }
    return data
  }
}

interface fetchPurchasedV2Response {
  modules: ModuleBasic[]
  subscriptions: Subscription[]
}
